import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { Arg, Subroutine } from '@/types/subroutine';
import { Variable } from '@/types/variable';
import { changeIndex, getRelevantNodes } from '@/util/array';
import { getHighestNumber } from '@/util/number';
import { isArray } from '@/util/typeGuards';
import { validateMemberType } from '@/util/validation/subroutines';
import { Button, Card, Stack } from '@mui/material';
import { FC } from 'react';
import ArgView from './ArgView';

type ArgumentsProps = {
  id: number;
  subroutine?: Subroutine;
  parentSubroutineId: number;
  updateApp: () => void;
};

const Arguments: FC<ArgumentsProps> = ({ updateApp, id, subroutine, parentSubroutineId }) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();

  const onArrowClick = (data: Arg[], index: number, arrowType: string) => {
    if (!subroutine?.args) return;

    const newIndex = arrowType === 'up' ? index - 1 : index + 1;
    const nodes = getRelevantNodes(application, metadata, subroutine.id, ['arguments']);

    nodes.forEach((node) => {
      if (application.blocks.details[node.node][node.value])
        application.blocks.details[node.node][node.value] = changeIndex(
          application.blocks.details[node.node][node.value] as [],
          index,
          newIndex
        );
    });

    application.blocks.event_handlers &&
      Object.entries(application.blocks.event_handlers).forEach(([eventId, eventValue]) => {
        Object.entries(eventValue).forEach(([id, value]) => {
          if (value.eventstart === subroutine.id && value.arguments.length && application.blocks.event_handlers) {
            changeIndex(application.blocks.event_handlers[eventId][id].arguments ?? [], index, newIndex);
          }
        });
      });

    subroutine.args = [...data];
    subroutine.arg_bindings = changeIndex([...subroutine.arg_bindings], index, newIndex);
    updateApp();
  };

  const updateArg = (newArg: Arg, newBinding: string) => {
    if (!subroutine) return;

    const index = subroutine.args.findIndex((arg) => arg.id === newArg.id);
    if (index < 0) return;

    subroutine.args = subroutine.args.with(index, newArg);
    subroutine.arg_bindings = subroutine.arg_bindings.with(index, newBinding);
    updateApp();
  };

  return (
    <Card variant='outlined' sx={{ p: 1 }}>
      <List
        subheader='Arguments'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'start'
        }}
      >
        {subroutine?.args.map((arg, index) => (
          <Stack key={`arg.item.${arg.id}`} direction='row' width='100%'>
            <ArgView
              id={id}
              subroutineId={parentSubroutineId}
              arg={arg}
              binding={subroutine.arg_bindings[index]}
              typeErrors={validateMemberType(subroutine, 'argument', arg, index)}
              data={subroutine.args}
              index={index}
              onArrowClick={(data, arrowType) => onArrowClick(data, index, arrowType)}
              onChange={updateArg}
              onDelete={() => {
                const nodes = getRelevantNodes(application, metadata, subroutine.id, ['arguments']);

                // Find and remove the block details value that matches with the removed argument
                nodes.forEach((node) => {
                  if (application.blocks.details[node.node][node.value]) {
                    const result = application.blocks.details[node.node][node.value] as (Variable | undefined)[];
                    result.splice(index, 1);
                  }
                });

                application.blocks.event_handlers &&
                  Object.entries(application.blocks.event_handlers).forEach(([eventId, eventValue]) => {
                    Object.entries(eventValue).forEach(([id, value]) => {
                      if (
                        value.eventstart === subroutine.id &&
                        value.arguments.length &&
                        application.blocks.event_handlers
                      ) {
                        application.blocks.event_handlers[eventId][id]?.arguments?.splice(index, 1);
                      }
                    });
                  });

                subroutine.args = subroutine.args.filter((_, i) => i !== index);
                subroutine.arg_bindings = subroutine.arg_bindings.filter((_, i) => i !== index);
                updateApp();
              }}
            />
          </Stack>
        ))}
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            if (subroutine) {
              const newArgId = getHighestNumber(subroutine.args, 'id') + 1;
              subroutine.arg_bindings = [...subroutine.arg_bindings, ''];
              subroutine.args = [...subroutine.args, { id: newArgId, label: '', type: '' }];
              Object.entries(application?.blocks.nodes || []).forEach(([id, node]) => {
                if (node.type === 'Gosub' || node.type === 'RunTask') {
                  if (
                    application?.blocks.details[id] &&
                    application?.blocks.details[id].substart === subroutine.id &&
                    isArray(application.blocks.details[id].arguments)
                  ) {
                    application.blocks.details[id].arguments = [...application.blocks.details[id].arguments, null];
                  }
                }
              });
              updateApp();
            }
          }}
        >
          Add argument
        </Button>
      </List>
    </Card>
  );
};

export default Arguments;
